<script lang="ts">
	import { format } from 'svelte-i18n';
	import { Alert, Slider, Toggle, SelectCard, CardGrid } from '@Shelly/shelly-design-system';
	import { createEventDispatcher } from 'svelte';
	import { fill_empty_actions, update_actions } from './lights_actions_helpers';
	import WithColorAndTempWheelsV2 from './WithColorAndTempWheelsV2.svelte';
	import TempWheelWithButtonsV2 from './TempWheelWithButtonsV2.svelte';
	import Timer from './TimerV2.svelte';
	import { capFirstChar } from 'src/helpers/utilities';
	import TransitionDurationV2 from './TransitionDurationV2.svelte';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';

	export let actions = []; //both options - select percent level or change rooler/cover state
	export let active_actions = []; // active action must be the one of both provided options
	export let sceneIsValid;
	// Shared wizard state. Passed down so the "Timer custom settings" option can
	// live inside the change-state section (FE-1563) instead of at the bottom of
	// the whole light step, which incorrectly attached it to brightness/colour.
	export let sceneData: any = null;

	/** Action ids that represent the on/off/toggle "Change state" section. */
	const CHANGE_STATE_ACTION_IDS = ['turn', 'state'];

	const dispatch = createEventDispatcher();

	const mappedActionIcons = {
		0: 'fas fa-power-off text-success',
		1: 'fas fa-power-off text-danger',
		2: 'fal fa-toggle-on',
	};

	let prepareActions;

	/**
	 * Action names that belong to the unified `set light` control
	 * (mode + rgb channels + color temperature). `ct` is included so legacy
	 * scenes saved before the split UI was unified (see FE-1730) still match.
	 */
	const SET_LIGHT_ACTION_NAMES = ['mode', 'r', 'g', 'b', 'temp', 'ct'];

	function _prepareActions() {
		prepareActions = actions.map((_act) => {
			let checked = active_actions?.find((el) => {
				return (
					el.name === _act.id ||
					(_act.id === 'set light' && SET_LIGHT_ACTION_NAMES.includes(el.name)) ||
					(_act.id === 'color' && ['r', 'g', 'b'].includes(el.name)) ||
					(_act.id === 'temp' && el.name === 'ct') ||
					(_act.id === 'state' && el.name === 'turn')
				);
			})
				? true
				: false;

			let related_actions = checked
				? active_actions?.filter((el) => {
						return (
							el.name === _act.id ||
							(_act.id === 'set light' && SET_LIGHT_ACTION_NAMES.includes(el.name)) ||
							(_act.id === 'color' && ['r', 'g', 'b'].includes(el.name)) ||
							(_act.id === 'temp' && el.name === 'ct') ||
							(_act.id === 'state' && el.name === 'turn')
						);
					})
				: [];

			return {
				id: _act.id,
				title: _act.title,
				checked,
				alert: _act.alert || false,
				values: _act?.values,
				range: _act?.range,
				selected_state: _act?.values
					? related_actions.find((el) => {
							if (_act.values.find((_e) => _e === el.value)) {
								return el;
							}
						})?.value || undefined
					: related_actions.find((el) => {
							if (_act.id === el.name) {
								return el;
							}
						})?.value || undefined,
				related_actions,
			};
		});
	}

	$: (_prepareActions(), actions, active_actions);

	const dispatch_new = () => {
		let new_active = prepareActions.reduce((_acc, _curr) => {
			if (_curr.checked) {
				_acc.push(..._curr.related_actions);
			}
			return _acc;
		}, []);

		dispatch('change_active', { new_active });
	};

	/**
	 * Handles checkbox state changes for action configuration.
	 * Initializes default action values when an action is first enabled.
	 *
	 * @param _ind - Index of the action in prepareActions array
	 */
	const handle_checkbox_change = (_ind) => {
		prepareActions[_ind].checked = !prepareActions[_ind].checked;

		let action = prepareActions[_ind];

		switch (action.id) {
			case 'transition_duration':
				prepareActions[_ind].selected_state = '';
				prepareActions[_ind].related_actions = [{ name: action.id, value: '' }];
				break;
			case 'state':
			case 'turn':
				prepareActions[_ind].selected_state = undefined;
				prepareActions[_ind].related_actions = [{ name: action.id, value: '' }];
				break;
			case 'brightness':
			case 'white_channel':
				prepareActions[_ind].selected_state = 0;
				prepareActions[_ind].related_actions = [{ name: action.id, value: 0 }];
				break;
			case 'temp':
				/**
				 * Use fill_empty_actions to map UI action ID 'temp' to correct action name.
				 * Returns 'ct' for CCT-only devices (Hilux/S3MX) and 'temp' for RGBW devices.
				 */
				prepareActions[_ind].related_actions = fill_empty_actions(action.id, 2700);
				break;
			case 'set light':
				prepareActions[_ind].related_actions = [
					{ name: 'r', value: 200 },
					{ name: 'g', value: 200 },
					{ name: 'b', value: 200 },
					{ name: 'mode', value: 'color' },
				];
				break;
			case 'color':
				prepareActions[_ind].related_actions = [
					{ name: 'r', value: 200 },
					{ name: 'g', value: 200 },
					{ name: 'b', value: 200 },
				];
				break;
		}

		dispatch_new();
	};

	const handle_radio_btn = (_ind, _val) => {
		if (!prepareActions[_ind].checked) prepareActions[_ind].checked = true;
		prepareActions[_ind].selected_state = _val;
		//here we know that the related actions has only one element because it is turn action
		if (prepareActions[_ind].related_actions.length > 0) {
			prepareActions[_ind].related_actions[0].value = _val;
		} else {
			prepareActions[_ind].related_actions.push({
				name: prepareActions[_ind].id,
				value: _val,
			});
		}
		dispatch_new();
	};

	const handle_slider_data = (e, _ind) => {
		let _val = e.detail.value || 0;

		if (!prepareActions[_ind].checked) prepareActions[_ind].checked = true;
		prepareActions[_ind].selected_state = _val;
		//here we know that the related actions has only one element because it is turn action
		if (prepareActions[_ind].related_actions.length > 0) {
			prepareActions[_ind].related_actions[0].value = _val;
		} else {
			prepareActions[_ind].related_actions.push({
				name: prepareActions[_ind].id,
				value: _val,
			});
		}
		dispatch_new();
	};

	/** OLD HANDLER */

	const handle_wheels_changes = (e, _ind) => {
		const { mode, value } = e.detail;

		if (!prepareActions[_ind].checked) prepareActions[_ind].checked = true;
		//1. if related_actions.lenght === 0 => fill with incomming data
		//2. else -> update actions
		let act_by_ind = prepareActions[_ind];
		if (act_by_ind.related_actions.length === 0) {
			prepareActions[_ind].related_actions = [...fill_empty_actions(act_by_ind.id, value, mode)];
		} else {
			prepareActions[_ind].related_actions = [
				...update_actions(act_by_ind.related_actions, act_by_ind.id, value, mode),
			];
		}
		dispatch_new();
	};

	//for future use if component is different from CheckBoxGroupElements
	const _prepare_props_by_id = (id, _val) => {
		switch (id) {
			case 'turn':
			case 'state':
				if (_val === 'toggle') {
					return $format('scene_action_toggle_state', {
						default: 'toggle the state of Shelly device',
					});
				}
				return $format('scene_action_turn_state', {
					values: { turn_state: $format(`label_action_turn_${_val}`) },
				});

			default:
				return {};
		}
	};
	const a11yProps = {
		ariaLabel: $format('label_adjust_value', { default: 'Adjust value' }),
		ariaLabelValue: null,
	};
</script>

{#if prepareActions.length > 0}
	{#each prepareActions as _act, _ind}
		{@const isToggleDisabled = !_act.checked}
		<Toggle
			checked={_act.checked}
			on:click={() => handle_checkbox_change(_ind)}
			size="l"
			class="mb-s"
			id="toggle-c46xvco"
		>
			{capFirstChar($format(_act.title))}
		</Toggle>

		{#if _act.alert}
			<Alert class="mb-s" heading={_act.alert} type={'info'} />
		{/if}
		{#if _act.values && _act.values.length > 0}
			<CardGrid sm={200} md={200} xl={200} noPaddingBottom>
				{#each _act.values as _val, _i}
					<div class="d-flex">
						<SelectCard
							cardBgColor={$showNewLayout ? 'color-1' : undefined}
							class="width-full"
							type="horizontal"
							variant="radio"
							title={`${_prepare_props_by_id(_act.id, _val)}`}
							icon={mappedActionIcons[_i]}
							selected={_act.selected_state === _val}
							on:click={() => {
								handle_radio_btn(_ind, _val);
							}}
							disabled={isToggleDisabled}
						/>
					</div>
				{/each}
			</CardGrid>
		{:else if _act.id === 'set light'}
			<WithColorAndTempWheelsV2
				type="change_mode"
				active_actions={_act.related_actions}
				range={_act.range || [3000, 6500]}
				on:change={(e) => handle_wheels_changes(e, _ind)}
				disabled={isToggleDisabled}
			/>
		{:else if _act.id === 'color'}
			<WithColorAndTempWheelsV2
				active_actions={_act.related_actions}
				predefined="color"
				on:change={(e) => handle_wheels_changes(e, _ind)}
				disabled={isToggleDisabled}
			/>
		{:else if _act.id === 'temp'}
			<TempWheelWithButtonsV2
				value={_act.related_actions[0]?.value}
				range={_act.range || [2700, 6500]}
				on:change={(e) => handle_wheels_changes(e, _ind)}
				disabled={isToggleDisabled}
			/>
		{:else if _act.id === 'transition_duration'}
			<TransitionDurationV2
				bind:sceneIsValid
				bind:checked={_act.checked}
				value={_act.related_actions[0]?.value}
				on:change={(e) => (_act.related_actions[0].value = e.detail.value)}
			/>
		{:else}
			<Slider
				id="slider-qufiydv"
				{a11yProps}
				min={0}
				max={100}
				title={capFirstChar($format(_act.title))}
				value={_act.selected_state || 0}
				type="simple"
				on:value_change={(e) => handle_slider_data(e, _ind)}
				disabled={isToggleDisabled}
			/>
		{/if}

		{#if sceneData && _act.checked && CHANGE_STATE_ACTION_IDS.includes(_act.id)}
			<Timer bind:sceneData {active_actions} />
		{/if}

		{#if _ind < prepareActions.length - 1}
			<hr class="mb-m mt-m" />
		{/if}
	{/each}
{/if}
